import type { Router, RouteRecordRaw, RouterScrollBehavior } from 'vue-router';

import { scrollToHash } from '#/lib/scrollToHash';
import { useAuthStore } from '#/stores/auth';
import { useCommerceStore } from '#/stores/commerce';
import { useEventStore } from '#/stores/event';

/**
 * FO 라우터 정의 — routes·scrollBehavior·인증 가드를 export 한다(Router 인스턴스는 생성하지 않음).
 *
 * vite-ssg(SSG) 전환: ViteSSG 가 routes 로 Router 를 생성한다(빌드=memoryHistory, 브라우저=webHistory).
 * 따라서 이 모듈은 createRouter 를 호출하지 않고 옵션·가드만 제공하며, main.ts 가 ViteSSG 에 넘긴다.
 *
 * 레이아웃: meta.layout = 'default'(셸) | 'blank'(셸 없음). App.vue 리졸버가 선택.
 * 인증: meta.requiresAuth/requiresVerified 라우트는 registerAuthGuard 가 보호(미인증 → /error/auth-required).
 * 제목: meta.title 은 홈을 제외한 모든 화면 라우트에 필수(useRouteHead 가 `<title>` 조립 — types/router.d.ts 참조).
 *   자체 head 를 발행하는 라우트(가이드·약관·이벤트)만 생략한다.
 */

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('#/pages/fo/HomePage.vue'),
    meta: {
      layout: 'default',
      description:
        '대학 합격의 길 — 전국 120여 개 대학의 전형별 합격선·경쟁률·지원 전략을 담은 대학별 입시 전략 리포트. 수시·정시 성적 분석과 입시 가이드까지 한곳에서.',
    },
  },
  {
    // 한시 무료 이벤트 안내(050) — 익명 접근(외부 공유 링크 FR-004). 진입 시 참가 마커 저장(FR-002).
    path: '/event',
    name: 'event',
    component: () => import('#/pages/fo/EventPage.vue'),
    meta: { layout: 'default' },
  },
  {
    // 면접 로드맵 무료 이벤트 안내(081) — 익명 접근(공유 링크). 050 /event 와 독립(문구·OG 비공유).
    path: '/event/interview',
    name: 'event-interview',
    component: () => import('#/pages/fo/InterviewEventPage.vue'),
    meta: { layout: 'default' },
  },
  {
    path: '/materials',
    name: 'materials',
    component: () => import('#/pages/fo/MaterialsPage.vue'),
    meta: {
      layout: 'default',
      title: '대학 검색',
      description:
        '전국 120여 개 대학 리포트를 지역·계열·학교명으로 찾아 담고 구매하세요. 대학별 전형 분석·합격선·지원 전략 리포트.',
    },
  },
  {
    // 상품군 구매(079) — 고정 구성 다중 선택. 둘러보기는 익명 허용, 견적·결제는 페이지 안에서 로그인 유도.
    // 구성 선택을 ?options= 로 동기화하므로 선택할 때마다 상단으로 튀지 않게 스크롤을 유지한다.
    path: '/products/:groupCode',
    name: 'product-group',
    component: () => import('#/pages/fo/ProductGroupPage.vue'),
    meta: { layout: 'default', title: '상품 구매', preserveScrollOnQuery: true },
  },
  {
    path: '/packages',
    name: 'packages',
    component: () => import('#/pages/fo/PackagesPage.vue'),
    meta: { layout: 'default', requiresVerified: true, title: '리포트 구매' },
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('#/pages/fo/CheckoutPage.vue'),
    meta: { layout: 'default', requiresVerified: true, blockedInFreeMode: true, title: '결제' },
  },
  {
    path: '/checkout/result',
    name: 'checkout-result',
    component: () => import('#/pages/fo/CheckoutResultPage.vue'),
    meta: {
      layout: 'default',
      requiresVerified: true,
      blockedInFreeMode: true,
      title: '결제 결과',
    },
  },
  // 구 마이페이지 '내 정보'(006 W-FO-16) — #167 로 폐기. 입시정보 편집·파기의 소유는
  // 내 입시카드(035 WS-01) 하나다. 북마크·기존 링크 동선 보존을 위해 리다이렉트만 남긴다.
  {
    path: '/mypage/profile',
    redirect: '/analysis/card',
  },
  {
    path: '/mypage/library',
    name: 'mypage-library',
    component: () => import('#/pages/fo/MyLibraryPage.vue'),
    meta: { layout: 'default', requiresVerified: true, title: '내 자료실' }, // T023: VERIFIED 상향(FR-015, pending 차단)
  },
  {
    path: '/mypage/orders',
    name: 'mypage-orders',
    component: () => import('#/pages/fo/MyOrdersPage.vue'),
    meta: { layout: 'default', requiresAuth: true, title: '구매내역' },
  },
  {
    path: '/mypage/inquiries',
    name: 'mypage-inquiries',
    component: () => import('#/pages/fo/MyInquiriesPage.vue'),
    meta: { layout: 'default', requiresAuth: true, title: '1:1 문의' },
  },
  {
    path: '/mypage/inquiries/:id',
    name: 'mypage-inquiry-detail',
    component: () => import('#/pages/fo/MyInquiryDetailPage.vue'),
    meta: { layout: 'default', requiresAuth: true, title: '문의 상세' },
  },
  {
    // 내 후기 — 026 US1 T023a. 본인 후기 수정 화면(/review/:id/edit)의 진입 경로(FR-018c-1).
    path: '/mypage/reviews',
    name: 'mypage-reviews',
    component: () => import('#/pages/fo/MyReviewsPage.vue'),
    meta: { layout: 'default', requiresAuth: true, title: '내 후기' },
  },
  {
    // 가이드 허브 — 주제형 가이드 목록 + 전문가 소개(#authority) 앵커.
    // :slug 보다 먼저 선언해 '/guide' 가 slug 로 잡히지 않게 한다.
    path: '/guide',
    name: 'guide-index',
    component: () => import('#/pages/fo/GuideIndexPage.vue'),
    meta: { layout: 'default' },
  },
  {
    path: '/guide/:slug',
    name: 'guide',
    component: () => import('#/pages/fo/GuidePage.vue'),
    meta: { layout: 'default' },
  },
  {
    // 공지사항 목록 — 026 US3(FR-034). 익명 열람 허용, 인증 가드 없음.
    path: '/notice',
    name: 'notice',
    component: () => import('#/pages/fo/NoticesPage.vue'),
    meta: {
      layout: 'default',
      title: '공지사항',
      description: '자료 개편·운영 안내 등 대학 합격의 길 공지사항.',
    },
  },
  {
    // 공지 상세 — 고유 URL 로 외부 전달 가능해야 함(FR-034a). :slug 라우트와 동일하게 목록 뒤 선언.
    path: '/notice/:id',
    name: 'notice-detail',
    component: () => import('#/pages/fo/NoticeDetailPage.vue'),
    meta: { layout: 'default', title: '공지사항' },
  },
  {
    // 입시 소식 목록 — 083 US1(FR-010). 공식 발표 해설, 익명 열람 허용(인증 가드 없음).
    path: '/news',
    name: 'news',
    component: () => import('#/pages/fo/NewsPage.vue'),
    meta: {
      layout: 'default',
      title: '입시 소식',
      description:
        '대학·교육 기관의 공식 입시 발표를 학생·학부모 눈높이로 정리한 대학 합격의 길 입시 소식.',
    },
  },
  {
    // 입시 소식 상세 — 공지(/notice/:id)와 경로를 공유하지 않는다(카테고리 불일치 번호는 404).
    path: '/news/:id',
    name: 'news-detail',
    component: () => import('#/pages/fo/NewsDetailPage.vue'),
    meta: { layout: 'default', title: '입시 소식' },
  },
  {
    // 후기 공개 목록 — 026 US1(FR-018). 익명 열람 허용, 인증 가드 없음.
    path: '/review',
    name: 'review',
    component: () => import('#/pages/fo/ReviewsPage.vue'),
    meta: {
      layout: 'default',
      title: '이용 후기',
      description: '대학별 입시 전략 리포트를 이용한 학생·학부모의 후기.',
    },
  },
  {
    // 후기 작성 — 인증 필요(FR-001). '/review/:id/edit' 보다 먼저 선언해 'write' 가 :id 로 잡히지 않게 한다.
    path: '/review/write',
    name: 'review-write',
    component: () => import('#/pages/fo/ReviewWritePage.vue'),
    meta: { layout: 'default', requiresAuth: true, title: '후기 작성' },
  },
  {
    // 본인 후기 수정 — 인증 필요, 본인 것만(백엔드 403). 진입 경로는 마이페이지 "내 후기"(FR-018c-1).
    path: '/review/:id/edit',
    name: 'review-edit',
    component: () => import('#/pages/fo/ReviewEditPage.vue'),
    meta: { layout: 'default', requiresAuth: true, title: '후기 수정' },
  },
  {
    path: '/legal/terms',
    name: 'legal-terms',
    component: () => import('#/pages/fo/legal/TermsPage.vue'),
    meta: {
      layout: 'default',
      description:
        '대학 합격의 길 서비스 이용약관 — 회원 가입, 리포트 구매·다운로드, 환불 기준 등 서비스 이용 조건을 안내합니다.',
    },
  },
  {
    path: '/legal/privacy',
    name: 'legal-privacy',
    component: () => import('#/pages/fo/legal/PrivacyPage.vue'),
    meta: {
      layout: 'default',
      description:
        '대학 합격의 길 개인정보처리방침 — 수집하는 개인정보 항목과 이용 목적, 보관 기간, 이용자 권리를 안내합니다.',
    },
  },
  {
    path: '/legal/business',
    name: 'legal-business',
    component: () => import('#/pages/fo/legal/BusinessPage.vue'),
    meta: {
      layout: 'default',
      description:
        '대학 합격의 길 운영사 데오럭스 주식회사의 사업자 정보와 통신판매업 신고 정보를 안내합니다.',
    },
  },
  // ── Blank 레이아웃(셸 없음): 인증·예외 ─────────────────────────
  {
    path: '/login',
    name: 'login',
    component: () => import('#/pages/fo/LoginPage.vue'),
    meta: { layout: 'blank', title: '로그인' },
  },
  {
    // 구글 SSO 로그인 성공 랜딩 — 백엔드 OAuth 콜백이 이 URL 로 리다이렉트.
    // HttpOnly 쿠키(IPSI_SESSION) 는 이미 설정된 상태. 페이지는 스토어 갱신 후 홈으로 이동.
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('#/pages/fo/auth/SsoCallbackPage.vue'),
    meta: { layout: 'blank', title: '로그인 처리 중' },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('#/pages/fo/SignupPage.vue'),
    meta: { layout: 'blank', title: '회원가입' },
  },
  {
    // 로컬 가입 직후 인증 안내(056) — 세션이 없는 상태로 도착한다.
    // 대상 정보는 라우터 state 로만 받는다(주소에 식별자 금지 — FR-002a). state 가 없으면 화면이 로그인으로 보낸다.
    path: '/signup/pending',
    name: 'signup-pending',
    component: () => import('#/pages/fo/auth/SignupPendingPage.vue'),
    meta: { layout: 'blank', title: '이메일 인증 안내' },
  },
  {
    // 메일 인증 링크 착지(056 — 003 에서 제거했던 라우트 복원).
    // 토큰은 fragment(#token=)로 도착한다 — 브라우저가 서버로 보내지 않아 액세스 로그·Referer 에
    // 남지 않는다. 화면이 읽는 즉시 주소에서 지우고, 서버에는 POST 본문으로 넘긴다.
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('#/pages/fo/auth/VerifyEmailPage.vue'),
    meta: { layout: 'blank', title: '이메일 인증' },
  },
  {
    path: '/error/auth-required',
    name: 'error-auth-required',
    component: () => import('#/pages/fo/error/AuthRequiredPage.vue'),
    meta: { layout: 'blank', title: '로그인이 필요합니다' },
  },
  {
    path: '/error/email-conflict',
    name: 'error-email-conflict',
    component: () => import('#/pages/fo/error/EmailConflictPage.vue'),
    meta: { layout: 'blank', title: '이메일 중복 안내' },
  },
  {
    path: '/error/verify-error',
    name: 'error-verify-error',
    component: () => import('#/pages/fo/error/VerifyErrorPage.vue'),
    meta: { layout: 'blank', title: '인증 오류' },
  },
  // 디자인 시스템 라이브 쇼케이스 — 헤더·푸터 비링크(URL 직접 진입). 개발·디자인 소통용.
  {
    path: '/design-system',
    name: 'design-system',
    component: () => import('#/pages/fo/DesignSystemPage.vue'),
    meta: { layout: 'default', title: '디자인 시스템' },
  },
  // ── 2차 AI 입시분석(analysis 도메인) — 워크스페이스 셸(layout='workspace') ────────
  // 서브도메인 배포 대비 경계. 데모라 인증 meta 미부여(누구나 열람).
  {
    path: '/analysis',
    name: 'analysis-home',
    component: () => import('#/pages/fo/analysis/AnalysisHomePage.vue'),
    meta: {
      layout: 'workspace',
      title: 'AI 입시분석 워크스페이스',
      description:
        '내 수능·내신 성적으로 지원 가능한 대학을 데이터로 확인하는 AI 입시분석. 정시·수시 분석, 수시 6장 구성, 진로심리검사를 한곳에서.',
    },
  },
  // 내 입시카드(035 WS-01) — 모든 분석의 공통 입력 자산. 본인 성적이라 인증 필요.
  {
    path: '/analysis/card',
    name: 'analysis-card',
    component: () => import('#/pages/fo/analysis/IpsiCardPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '내 입시카드' },
  },
  // 정시 분석(036 WS-02). 077 — 비로그인은 샘플 뷰, 회원 데이터 분기는 화면(페이지 스위치)이 세션으로
  // 소유한다. 라우트 가드를 두지 않아도 회원 데이터는 세션이 있어야만 조회된다(API 인증 무변경).
  {
    path: '/analysis/jeongsi',
    name: 'analysis-jeongsi',
    component: () => import('#/pages/fo/analysis/JeongsiAnalysisPage.vue'),
    meta: {
      layout: 'workspace',
      title: '정시 분석',
      description:
        '수능 성적으로 대학·학과별 지원 가능성을 확인하는 정시 분석. 최근 입시 결과와 대학별 수능 반영 방식을 함께 보여 줍니다.',
    },
  },
  // 구 정량분석 데모 경로 보존 — 목데이터 화면은 폐기하고 신규 화면으로 보낸다(FR-002).
  {
    path: '/analysis/quant-jeongsi',
    redirect: '/analysis/jeongsi',
  },
  // 정시 학과 AI 해석 재열람(065 WS-14) — 보관함에서 여는 과거 스냅샷 전용 화면. 본인 것만(서버 404 격리).
  {
    path: '/analysis/jeongsi/opinions/:id(\\d+)',
    name: 'analysis-jeongsi-opinion-detail',
    component: () => import('#/pages/fo/analysis/JeongsiOpinionDetailPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '정시 학과 AI 해석' },
  },
  // 수시 분석(040 WS-03). 077 — 비로그인은 샘플 뷰, 회원 데이터 분기는 화면이 세션으로 소유(정시와 동형).
  {
    path: '/analysis/susi',
    name: 'analysis-susi',
    component: () => import('#/pages/fo/analysis/SusiAnalysisPage.vue'),
    meta: {
      layout: 'workspace',
      title: '수시 분석',
      description:
        '내신 성적으로 대학·전형별 지원 가능성을 확인하는 수시 분석. 학생부교과·종합 전형의 입시 결과와 준비 방향을 함께 보여 줍니다.',
    },
  },
  // 수시 6장 카드 AI 리포트(060) — 조건 입력·사전 카운트·생성.
  {
    path: '/analysis/susi/cards',
    name: 'analysis-susi-cards',
    component: () => import('#/pages/fo/analysis/SusiCardsPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '수시 6장 카드' },
  },
  // 구 "지난 리포트" 화면(060) — 065 로 폐기. 이력은 리포트 보관함 하나로 수렴한다(FR-010).
  // 북마크·공유 링크 보존을 위해 수시 6장 필터로 리다이렉트만 남긴다.
  {
    path: '/analysis/susi/cards/history',
    redirect: { path: '/analysis/reports', query: { kind: 'SUSI_CARDS' } },
  },
  // 리포트 재열람 — 저장 스냅샷 재현(본인 것만, 서버 404 격리).
  {
    path: '/analysis/susi/cards/:reportId(\\d+)',
    name: 'analysis-susi-cards-report',
    component: () => import('#/pages/fo/analysis/SusiCardsPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '수시 6장 리포트' },
  },
  // 6장 카드 조정(063) — 저장 shortlist 안에서 카드 교체 → AI 재검토. 편집 상태는 페이지 로컬(서버 미저장).
  {
    path: '/analysis/susi/cards/:reportId(\\d+)/edit',
    name: 'analysis-susi-cards-edit',
    component: () => import('#/pages/fo/analysis/SusiCardsEditPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '수시 6장 편집' },
  },
  // 진단 리포트 상세(040 WS-04 조회 스택) — 본인 진단만(서버 404 격리).
  {
    path: '/analysis/diagnosis/:id',
    name: 'analysis-diagnosis-detail',
    component: () => import('#/pages/fo/analysis/DiagnosisDetailPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '진단 상세' },
  },
  // 통합 AI 분석(045 WS-08) — 재료 현황·지원 카드 바구니·종합 소견 생성. 본인 데이터라 인증 필요.
  {
    path: '/analysis/ai',
    name: 'analysis-ai',
    component: () => import('#/pages/fo/analysis/AiAnalysisPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: 'AI 종합 의견' },
  },
  // AI 소견 상세(045 WS-09) — 본인 소견만(서버 404 격리).
  {
    path: '/analysis/ai/opinions/:id',
    name: 'analysis-opinion-detail',
    component: () => import('#/pages/fo/analysis/OpinionDetailPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: 'AI 의견 상세' },
  },
  // 진로심리검사(022) 목록. 077 — 비로그인은 샘플 뷰(4종 소개·결과 발췌), 회원 데이터 분기는 화면이 세션으로
  // 소유. 응시·결과 자식 라우트는 본인 데이터라 인증을 유지한다.
  {
    path: '/analysis/tests',
    name: 'analysis-tests',
    component: () => import('#/pages/fo/analysis/TestCenterPage.vue'),
    meta: {
      layout: 'workspace',
      title: '진로심리검사',
      description:
        '진로·학습·심리·진학 4가지 검사로 나의 성향과 준비 상태를 확인하고 결과 리포트를 받아 보세요.',
    },
  },
  // 응시는 몰입 모드(043) — 워크스페이스 셸 제거(blank), 인증은 유지(본인 응시 데이터).
  {
    path: '/analysis/tests/:testCode',
    name: 'analysis-test-take',
    component: () => import('#/pages/fo/analysis/TestTakePage.vue'),
    meta: { layout: 'blank', requiresAuth: true, title: '검사 진행' },
  },
  {
    path: '/analysis/results/:resultId',
    name: 'analysis-test-result',
    component: () => import('#/pages/fo/analysis/TestResultPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '검사 결과' },
  },
  {
    path: '/analysis/reports',
    name: 'analysis-reports',
    component: () => import('#/pages/fo/analysis/ReportArchivePage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '리포트 보관함' },
  },
  // 구 프로토타입 경로 보존 — 공유 링크·북마크가 새 정시 정량분석으로 이어지도록 리다이렉트.
  {
    path: '/prototype/quant-recommend',
    redirect: '/analysis/jeongsi',
  },
  // 정의되지 않은 경로 — 홈 redirect 는 soft 404(홈 복제 문서)라 안내 화면 + noindex(useRouteHead)로 처리한다.
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('#/pages/fo/error/NotFoundPage.vue'),
    meta: { layout: 'blank', title: '페이지를 찾을 수 없습니다' },
  },
];

/**
 * 스크롤 복원/앵커 정책.
 * - 뒤로/앞으로(savedPosition): 브라우저 보존 위치로.
 * - 해시 앵커(예: 다른 페이지 → /#price): 라우터 기본 스크롤을 끄고(false), 레이아웃이
 *   정착한 뒤 scrollToHash 가 정확히 보정. 라우터의 1회성 좌표 계산은 위쪽 비동기 콘텐츠가
 *   부풀기 전에 끝나 어긋나므로 사용하지 않는다.
 * - 쿼리만 바뀐 같은 경로 이동 + meta.preserveScrollOnQuery: 현재 위치 유지(화면 상태의 URL 동기화).
 * - 그 외: 상단으로.
 */
export const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) return savedPosition;
  if (to.hash) {
    scrollToHash(to.hash);
    return false;
  }
  if (to.meta.preserveScrollOnQuery && to.path === from.path) return false;
  return { top: 0 };
};

/**
 * 인증 가드 등록 — 인증 수준(guest/pending/verified) × meta 요구를 분기.
 *   - requiresVerified: verified 만 통과. guest→로그인 유도, pending→메일 인증 유도.
 *   - requiresAuth: 로그인(pending|verified) 통과. guest 만 차단(로그인 유도).
 *   - blockedInFreeMode: 무료 개방 모드(029)면 /packages 로 리다이렉트. 인증 판정보다 먼저 본다 —
 *     무료 기간에 존재하지 않는 결제 화면 때문에 로그인부터 요구하는 건 잘못된 유도다(FR-029, R7).
 * 미인증 시 /error/auth-required 로 보내고 reason(login|verify)·원래 목적지(redirect)를 쿼리에 보존.
 * ⚠️ 클라 가드는 UX 차단일 뿐 — 실제 접근 강제는 백엔드 @PreAuthorize + VERIFIED 검증(IA 03:282).
 *
 * 부팅 세션 복원(FR-017·SC-011): 메모리 authLevel 은 새로고침마다 guest 로 초기화되므로, 첫 내비게이션에서
 * 영속 쿠키로 서버 세션을 1회 확인해 로그인 상태를 되살린다. restoreSession() 은 완료 플래그로 1회만 실제
 * 호출되며, 이를 보호 라우트 판정 전에 await 해야 유효 세션이 guest 로 오인돼 로그인 화면으로 튕기는 일을 막는다.
 *
 * SSG: 빌드 타임 프리렌더는 각 라우트를 직접 렌더하며 router.push 네비게이션이 없어 가드가 돌지 않는다.
 * 그래도 가드는 클라이언트(브라우저)에서만 등록해 빌드 환경에서 useAuthStore/restoreSession(API)이
 * 실행될 여지를 원천 차단한다(main.ts 가 isClient 일 때만 호출).
 */
export function registerAuthGuard(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore();

    // 커머스 설정 백그라운드 재검증(050) — 이벤트 활성이 시각 경계로 자동 전환되므로, 경계를 넘긴
    // 열린 탭이 낡은 값을 계속 쓰지 않도록 라우트 이동마다 갱신 기회를 준다(1분 스로틀·비차단·무음).
    useCommerceStore().revalidate();

    // 홈 진입 = 무료 의도 리셋(050 — 심사 UI 불변): 이벤트 무료 CTA 로 켜진 무료 수령 화면은 홈부터
    // 다시 시작하는 순간 결제 기본값으로 복귀한다 — 심사자가 이벤트를 구경한 뒤 결제 경로를 밟아도
    // 장바구니는 사전 전달한 캡처(결제 전용 화면)와 동일하다.
    if (to.name === 'home') {
      useEventStore().clearFreeIntent();
    }

    // 첫 진입 시 쿠키 세션 복원(이미 복원됐으면 즉시 반환). 보호 라우트 판정의 정확성을 위해 먼저 완료한다.
    if (!auth.sessionRestored) {
      await auth.restoreSession();
    }

    const { requiresAuth, requiresVerified, blockedInFreeMode } = to.meta;

    // 무료 개방 모드 차단(029 FR-029) — 결제 화면은 무료 기간에 성립하지 않으므로 장바구니로 되돌린다.
    // 설정 미도착 상태로 판정하면 무료 기간에도 결제 화면이 한 번 열리므로, 대상 라우트에 한해 로드를 기다린다
    // (main.ts 가 부트에서 이미 시작했으므로 대개 즉시 반환). 실패 시 freeMode=false 로 기존 유료 흐름 유지.
    if (blockedInFreeMode) {
      const commerce = useCommerceStore();
      await commerce.loadConfig().catch(() => {});
      if (commerce.freeMode) return { name: 'packages' };
      // 이벤트 무료 의도 차단(050 코드리뷰 P1) — 설정 도착 전 장바구니가 잠깐 결제 렌더였던 창에서
      // 결제하기를 눌렀더라도, 활성 이벤트 + 무료 의도면 결제 진입을 막고 장바구니(무료 렌더)로 되돌린다.
      // 결제 결과(checkout-result)는 제외 — 이미 승인된 결제의 확정 콜백 복귀를 막으면 미확정 결제가 남는다.
      if (to.name === 'checkout' && commerce.reviewEvent && useEventStore().freeIntent) {
        return { name: 'packages' };
      }
    }

    if (!requiresAuth && !requiresVerified) return true;

    const level = auth.authState;

    if (requiresVerified) {
      if (level === 'verified') return true;
      // guest=로그인부터, pending=메일 인증부터 — 유도 화면 분기 신호.
      const reason = level === 'guest' ? 'login' : 'verify';
      return {
        name: 'error-auth-required',
        query: { redirect: to.fullPath, reason },
      };
    }

    // requiresAuth — 로그인만 요구(pending 통과). guest 만 차단.
    if (level !== 'guest') return true;
    return {
      name: 'error-auth-required',
      query: { redirect: to.fullPath, reason: 'login' },
    };
  });
}
